// InboundCallPopup — global toast/modal that appears whenever a call hits
// one of CIVIX's inbound tracking numbers. Listens for the 'inbound:call'
// socket event emitted by api/src/routes/inboundRouting.js (/voice handler).
//
// Why both a toast AND a modal:
//   - Toast lets the agent keep doing what they're doing while still seeing
//     the caller's name/state/product. Auto-dismisses after 25s.
//   - "Open Lead" jumps straight to the lead record so the agent can start
//     the conversation with full context.
//
// Multiple concurrent calls are supported (stacked toasts).
//
// Sound: a short, gentle tone plays once per call. Suppressed if the user
// has muted notifications in PhonePanel (we read `civix_phone_muted`
// from localStorage, same key PhonePanel uses).

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSocket } from '../lib/socket.js';

const POPUP_TTL_MS = 25_000; // auto-dismiss after this many ms

function formatPhone(p) {
  if (!p) return '';
  const digits = String(p).replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return p;
}

function playRingtone() {
  try {
    if (localStorage.getItem('civix_phone_muted') === '1') return;
    // Soft "ding" — generated tone, no asset dependency.
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = 880;
    gain.gain.value = 0.0001;
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    gain.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.05);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.6);
    osc.stop(ctx.currentTime + 0.65);
    setTimeout(() => ctx.close(), 800);
  } catch {
    // Audio context may be blocked until user interaction — that's OK.
  }
}

export default function InboundCallPopup() {
  const navigate = useNavigate();
  const [calls, setCalls] = useState([]); // active call objects

  const dismiss = useCallback((callId) => {
    setCalls((prev) => prev.filter((c) => c.callId !== callId));
  }, []);

  useEffect(() => {
    const socket = getSocket();
    if (!socket || !socket.on) return undefined;

    const onInbound = (payload) => {
      if (!payload || !payload.callId) return;
      // De-dupe: if we've already shown this callId, ignore.
      setCalls((prev) => {
        if (prev.some((c) => c.callId === payload.callId)) return prev;
        return [{ ...payload, receivedAt: Date.now() }, ...prev].slice(0, 4);
      });
      playRingtone();
      // Auto-dismiss after TTL so toasts don't accumulate forever
      // (e.g. agent stepped away during a busy hour).
      setTimeout(() => dismiss(payload.callId), POPUP_TTL_MS);
    };

    socket.on('inbound:call', onInbound);
    return () => {
      if (socket.off) socket.off('inbound:call', onInbound);
    };
  }, [dismiss]);

  const handleOpenLead = (call) => {
    dismiss(call.callId);
    if (call.leadId) {
      navigate(`/leads/${call.leadId}`);
    } else {
      navigate('/leads');
    }
  };

  if (calls.length === 0) return null;

  return (
    <div
      className="fixed top-20 right-4 z-1000 flex flex-col gap-3 max-w-sm w-88 pointer-events-none"
      aria-live="polite"
    >
      {calls.map((call) => (
        <div
          key={call.callId}
          className="pointer-events-auto rounded-lg shadow-2xl border border-civix-purple/50 bg-linear-to-br from-[#1a0a2e] to-[#0a0a1a] backdrop-blur-sm p-4 animate-pulse-once"
          style={{ boxShadow: '0 0 24px rgba(140, 80, 255, 0.35)' }}
        >
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-civix-purple opacity-75" />
                <span className="relative inline-flex rounded-full h-3 w-3 bg-civix-purple" />
              </span>
              <span className="text-[11px] uppercase font-bold tracking-widest text-civix-purple">
                CIVIX inbound lead
              </span>
            </div>
            <button
              type="button"
              onClick={() => dismiss(call.callId)}
              className="text-white/40 hover:text-white text-lg leading-none px-1"
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>

          <div className="text-white font-semibold text-base leading-tight mb-1">
            {call.leadName || 'Unknown caller'}
          </div>
          <div className="text-white/70 text-sm mb-2">
            {formatPhone(call.fromNumber)}
          </div>

          {(call.state || call.product) && (
            <div className="flex flex-wrap gap-1.5 mb-3">
              {call.state && (
                <span className="text-[10px] uppercase tracking-wide bg-white/10 text-white/80 px-2 py-0.5 rounded-sm">
                  {call.state}
                </span>
              )}
              {call.product && (
                <span className="text-[10px] uppercase tracking-wide bg-white/10 text-white/80 px-2 py-0.5 rounded-sm">
                  {call.product}
                </span>
              )}
              {call.leadCreated && (
                <span className="text-[10px] uppercase tracking-wide bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded-sm">
                  New lead
                </span>
              )}
              {call.forYou && (
                <span className="text-[10px] uppercase tracking-wide bg-civix-purple/30 text-civix-purple px-2 py-0.5 rounded-sm">
                  Routed to you
                </span>
              )}
            </div>
          )}

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => handleOpenLead(call)}
              className="flex-1 bg-civix-purple hover:bg-civix-purple/80 text-white text-sm font-semibold py-2 rounded-sm transition"
            >
              Open lead
            </button>
            <button
              type="button"
              onClick={() => dismiss(call.callId)}
              className="px-3 py-2 text-white/60 hover:text-white text-sm rounded-sm border border-white/10"
            >
              Later
            </button>
          </div>

          <div className="text-[10px] text-white/40 mt-2">
            via {call.routingName || 'tracking number'} · {formatPhone(call.toNumber)}
          </div>
        </div>
      ))}
    </div>
  );
}
